import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

interface ImageProps {
  src: string;
  alt: string;
  className?: string;
}

const LazyImage: React.FC<ImageProps> = ({ src, alt, className = "" }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(document.getElementById(src) as Element);

    return () => {
      observer.disconnect();
    };
  }, [src]);

  return (
    <div id={src} className={`relative overflow-hidden ${className}`}>
      {isInView && !hasError && (
        <motion.img
          src={src}
          alt={alt}
          initial={{ opacity: 0, filter: "blur(20px)" }}
          animate={{
            opacity: isLoaded ? 1 : 0,
            filter: isLoaded ? "blur(0px)" : "blur(20px)",
          }}
          transition={{ duration: 0.4 }}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          className="w-full h-full object-cover"
        />
      )}
      {!isLoaded && !hasError && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
      {hasError && (
        <div className="flex items-center justify-center bg-red-50">
          <div className="text-center text-[#FF2C2C] p-4">
            <svg className="w-12 h-12 text-red-400 mx-auto " fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-red font-medium text-sm">Error while loading!</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LazyImage;
